import { Link, Navigate } from "@tanstack/react-router";
import { UserRound } from "lucide-react";
import { useMemo, useState } from "react";
import { ReportError } from "@/components/reports/hours/ReportError";
import { ReportFreshness } from "@/components/reports/hours/ReportFreshness";
import { ReportRangePicker } from "@/components/reports/hours/ReportRangePicker";
import {
  ResourceDemandsTable,
  ResourceMonthDetail,
} from "@/components/reports/hours/ResourceDemandTables";
import { ResourceHoursChart } from "@/components/reports/hours/ResourceHoursChart";
import { hrs, pct } from "@/components/reports/hours/report-format";
import { EmptyState } from "@/components/shared/empty-state";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { SearchInput } from "@/components/shared/search-input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type ResourceHoursReport,
  toQueryString,
  useResourceHours,
  useResourceHoursDirectory,
} from "@/hooks/use-hours-reports";
import type { RangeSearch } from "@/lib/report-search";

const PERSON_TYPE_LABEL = {
  fullTime: "Full time",
  variable: "Variable",
  partTime: "Part time",
  unknown: "Schedule type not set",
} as const;

/**
 * Reports → Resource Hours, no one picked yet. Whole-book roles choose anyone with
 * capacity or hours this month; everyone else goes straight to their own hours.
 */
export function ResourceHoursIndexPage() {
  const { data, isLoading, isError, error, refetch } = useResourceHoursDirectory();
  const [q, setQ] = useState("");
  const people = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.people ?? []).filter((p) => !needle || p.name.toLowerCase().includes(needle));
  }, [data, q]);

  if (data && !data.canSeeAll && data.selfPersonId) {
    return (
      <Navigate
        to="/dashboard/reports/resources/$personId"
        params={{ personId: data.selfPersonId }}
        replace
      />
    );
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Reports"
        title="Resource Hours"
        description="One person's Guidance, logged hours and capacity, Demand by Demand."
        actions={
          data?.canSeeAll ? (
            <SearchInput
              value={q}
              onValueChange={setQ}
              placeholder="Find a Resource…"
              className="h-9 w-64"
            />
          ) : null
        }
      />
      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : isError ? (
        <ReportError error={error} onRetry={() => refetch()} />
      ) : !data?.canSeeAll ? (
        <EmptyState
          icon={UserRound}
          title="Your sign-in is not linked to a Resource in NOS"
          description="Ask a Resource Manager to link your Resource to NOS, then your hours appear here."
        />
      ) : people.length === 0 ? (
        <EmptyState icon={UserRound} title="No one matches" description="Try another name." />
      ) : (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {people.map((p) => (
            <li key={p.personId}>
              <Link
                to="/dashboard/reports/resources/$personId"
                params={{ personId: p.personId }}
                className="flex flex-col rounded-lg border border-border bg-card px-3 py-2 transition hover:border-primary/40"
              >
                <span className="text-sm font-medium">{p.name}</span>
                <span className="text-xs text-muted-foreground">
                  {p.homeRoleName ?? "No home Role"}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </PageContainer>
  );
}

function Headline({ report }: { report: ResourceHoursReport }) {
  const current = report.monthTotals.find((t) => t.basis === "inProgress");
  const forward = report.monthTotals.filter((t) => t.basis === "forecast");
  const peakOver = Math.max(0, ...forward.map((t) => t.overallocatedHours));
  const past = report.monthTotals.filter((t) => t.basis === "actual");
  const g = past.reduce((s, t) => s + t.guidanceHours, 0);
  const a = past.reduce((s, t) => s + (t.actualHours ?? 0), 0);
  const stats = [
    {
      label: "This month",
      value: current ? `${hrs(current.actualHours)} of ${hrs(current.guidanceHours)}` : "–",
      hint: "Hours logged so far against this month's Guidance.",
    },
    {
      label: "Guidance % (past months)",
      value: pct(g > 0 ? a / g : null),
      hint: "Σ Actuals ÷ Σ Guidance over the closed months in range.",
    },
    {
      label: "Peak overallocation ahead",
      value: hrs(peakOver),
      hint: "The most hours guided beyond capacity in any forecast month.",
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {stats.map((s) => (
        <div
          key={s.label}
          title={s.hint}
          className="rounded-lg border border-border bg-card px-4 py-3"
        >
          <div className="text-xs text-muted-foreground">{s.label}</div>
          <div className="font-display text-xl font-semibold tabular-nums text-heading">
            {s.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Reports → Resource Hours for one person (spec C3). */
export function ResourceHoursPage({
  personId,
  search,
  setSearch,
}: {
  personId: string;
  search: RangeSearch;
  setSearch: (next: RangeSearch) => void;
}) {
  const qs = toQueryString({ from: search.from, to: search.to });
  const { data, isLoading, isError, error, refetch } = useResourceHours(personId, qs);
  const [month, setMonth] = useState<string | null>(null);
  const selected = month ?? data?.currentMonth ?? null;
  const total = data?.monthTotals.find((t) => t.month === selected);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Reports · Resource Hours"
        title={data?.person.name ?? "Resource Hours"}
        description={
          data
            ? `${data.person.homeRoleName ?? "No home Role"} · ${PERSON_TYPE_LABEL[data.person.personType]}. Click a month for its Demand-by-Demand detail.`
            : "Guidance, logged hours and capacity, Demand by Demand."
        }
        actions={
          data ? (
            <ReportRangePicker
              range={data.range}
              onChange={(r) => setSearch({ from: r.from, to: r.to })}
            />
          ) : null
        }
      />
      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : isError ? (
        <ReportError error={error} onRetry={() => refetch()} />
      ) : data ? (
        <>
          <Headline report={data} />
          <div className="rounded-lg border border-border bg-card p-4">
            <ResourceHoursChart
              totals={data.monthTotals}
              selectedMonth={selected}
              onSelectMonth={setMonth}
            />
            <div className="mt-2">
              <ReportFreshness dataAsOf={data.dataAsOf} />
            </div>
          </div>
          {selected && total && (
            <ResourceMonthDetail month={selected} total={total} demands={data.demands} />
          )}
          <section className="flex flex-col gap-3">
            <h2 className="font-display text-lg font-semibold text-heading">
              Active Demands in range
            </h2>
            {data.demands.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No Guidance or logged hours on an Active Demand in this range.
              </p>
            ) : (
              <ResourceDemandsTable demands={data.demands} />
            )}
          </section>
        </>
      ) : null}
    </PageContainer>
  );
}
